import { Stack, styled } from '@mui/material';

import { scrollbarStyles, theme } from '@/shared/design-system';
import { forMobile } from '@/styles/responsive';

export const PickerRoot = styled(Stack)({
  flexDirection: 'column',
  gap: '16px',
  minWidth: 0,
});

export const PickerToolbar = styled(Stack)(({ theme: muiTheme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '16px',

  [forMobile(muiTheme)]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

export const SearchField = styled(Stack)(({ theme: muiTheme }) => ({
  width: '320px',
  maxWidth: '100%',

  [forMobile(muiTheme)]: {
    width: '100%',
  },
}));

export const GroupList = styled(Stack)({
  flexDirection: 'column',
  maxHeight: '620px',
  overflowY: 'auto',
  paddingRight: '4px',
  ...scrollbarStyles,
});

export const GroupLabel = styled('span')({
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  paddingRight: '12px',
  minWidth: 0,
});

export const GroupLabelMeta = styled('span')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing[12],
  flexShrink: 0,
});

export const GroupCount = styled('span')({
  minWidth: '40px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
});

export const GroupMeter = styled('span')(({ theme: muiTheme }) => ({
  display: 'block',
  width: '96px',

  [forMobile(muiTheme)]: {
    width: '48px',
  },
}));

export const PermissionRows = styled(Stack)({
  flexDirection: 'column',
  paddingBottom: '8px',
});

export const BulkActionsRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px',
  padding: '4px 12px 12px',
});

const CHANGE_MARKER = {
  added: theme.colors.brand.solid,
  removed: theme.colors.error.solid,
} as const;

export const PermissionRowRoot = styled(Stack, {
  shouldForwardProp: (prop) => prop !== '$change',
})<{ $change: 'added' | 'removed' | null }>(({ $change }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '12px',
  borderRadius: theme.radius[8],
  minWidth: 0,
  // A staged row reads as "moved" without shouting: a neutral lift plus a coloured edge.
  backgroundColor: $change ? theme.colors.background.raised : 'transparent',
  boxShadow: $change ? `inset 2px 0 0 ${CHANGE_MARKER[$change]}` : 'none',
  transition: theme.motion.hover,

  '& + &': {
    borderTop: `1px solid ${theme.colors.border.subtle}`,
  },

  '&:hover': {
    backgroundColor: theme.colors.background.raised,
  },
}));

export const PermissionRowTitle = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  minWidth: 0,
});

export const PermissionRowBadges = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing[4],
});

export const PermissionRowText = styled(Stack)({
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
});

export const PermissionRowControl = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
});

export const LockedControl = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
  color: theme.colors.text.tertiary,

  '& svg': {
    width: '16px',
    height: '16px',
  },
});

export const EmptyState = styled(Stack)({
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  padding: '32px 16px',
  textAlign: 'center',
  borderRadius: theme.radius[12],
  backgroundColor: theme.colors.background.canvas,
});
